import { useEffect, useState, useRef } from 'react';
import { MessageSquare, Bot, UserCircle } from 'lucide-react';
import { fetchConversations, fetchConversation } from '../api/placeholder';

function formatTimestamp(ts) {
  const date = new Date(ts);
  const now = new Date();
  const diffMs = now - date;
  const diffHrs = diffMs / (1000 * 60 * 60);

  if (diffHrs < 24) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function formatMessage(text) {
  if (!text) return null;

  // Split into lines, process each
  return text.split('\n').map((line, lineIdx) => {
    // Remove leading # characters (markdown headers)
    const cleaned = line.replace(/^#+\s*/, '');

    // Parse *bold* segments
    const parts = [];
    let remaining = cleaned;
    let key = 0;

    while (remaining.length > 0) {
      const starIdx = remaining.indexOf('*');
      if (starIdx === -1) {
        parts.push(remaining);
        break;
      }

      // Text before the asterisk
      if (starIdx > 0) {
        parts.push(remaining.slice(0, starIdx));
      }

      // Find closing asterisk
      const closeIdx = remaining.indexOf('*', starIdx + 1);
      if (closeIdx === -1) {
        // No closing asterisk, treat as plain text
        parts.push(remaining.slice(starIdx));
        break;
      }

      const boldText = remaining.slice(starIdx + 1, closeIdx);
      parts.push(
        <strong key={`b${lineIdx}-${key++}`} className="font-semibold">
          {boldText}
        </strong>
      );
      remaining = remaining.slice(closeIdx + 1);
      continue;
    }

    return (
      <span key={lineIdx}>
        {lineIdx > 0 && <br />}
        {parts}
      </span>
    );
  });
}

function stripFormatting(text) {
  if (!text) return '';
  return text.replace(/\*/g, '').replace(/^#+\s*/gm, '');
}

function ConversationList({ conversations, selectedId, onSelect }) {
  return (
    <div className="flex flex-col">
      <div className="border-b border-surface-600 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-200">Conversations</h2>
        <p className="text-xs text-slate-500">{conversations.length} sessions</p>
      </div>
      <div className="flex-1 overflow-y-auto">
        {conversations.map((conv) => (
          <button
            key={conv.session_id}
            onClick={() => onSelect(conv.session_id)}
            className={`w-full border-b border-surface-700 px-4 py-3 text-left transition-all ${
              selectedId === conv.session_id
                ? 'bg-neon-cyan/5 border-l-2 border-l-neon-cyan'
                : 'hover:bg-surface-700/50'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-200">
                {conv.session_id}
              </span>
              <span className="text-xs text-slate-500">
                {formatTimestamp(conv.last_message_at)}
              </span>
            </div>
            <p className="mt-1 truncate text-xs text-slate-500">
              {stripFormatting(conv.last_message)}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

function ChatView({ sessionId, messages }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  if (!sessionId) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">
        <div className="text-center">
          <MessageSquare size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Select a conversation to view messages</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-surface-600 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-200">{sessionId}</h2>
        <p className="text-xs text-slate-500">{messages.length} messages</p>
      </div>
      <div ref={scrollRef} className="chat-scroll flex-1 space-y-4 overflow-y-auto p-4">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex items-start gap-2.5 ${msg.type === 'human' ? 'flex-row-reverse' : ''}`}
          >
            {/* Avatar */}
            <div
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                msg.type === 'human'
                  ? 'bg-neon-purple/10 text-neon-purple'
                  : 'bg-neon-cyan/10 text-neon-cyan'
              }`}
            >
              {msg.type === 'human' ? <UserCircle size={16} /> : <Bot size={16} />}
            </div>

            {/* Bubble */}
            <div
              className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                msg.type === 'human'
                  ? 'bg-neon-purple/15 text-slate-200 border border-neon-purple/20 rounded-tr-md'
                  : 'bg-surface-700 text-slate-300 border border-surface-600 rounded-tl-md'
              }`}
            >
              <p>{formatMessage(msg.content)}</p>
              <p
                className={`mt-1.5 text-[10px] ${
                  msg.type === 'human' ? 'text-neon-purple/50' : 'text-slate-600'
                }`}
              >
                {new Date(msg.created_at).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Conversations() {
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => {
    fetchConversations()
      .then(setConversations)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = (sessionId) => {
    setSelectedId(sessionId);
    setChatLoading(true);
    setMessages([]);
    fetchConversation(sessionId)
      .then(setMessages)
      .catch(() => setMessages([]))
      .finally(() => setChatLoading(false));
  };

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-8rem)] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-neon-cyan border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-100">WhatsApp Conversations</h1>
          <p className="text-sm text-slate-500">View user chat sessions with Dr. Mali AI</p>
        </div>
        <div className="rounded-2xl border border-surface-600 bg-surface-800 p-8 text-center">
          <p className="text-sm text-neon-pink">{error}</p>
          <p className="mt-2 text-xs text-slate-500">Make sure the n8n workflow is active and listening.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-100">WhatsApp Conversations</h1>
        <p className="text-sm text-slate-500">View user chat sessions with Dr. Mali AI</p>
      </div>

      <div className="grid h-[calc(100vh-200px)] grid-cols-1 gap-0 overflow-hidden rounded-2xl border border-surface-600 bg-surface-800 md:grid-cols-[320px_1fr]">
        {/* Left panel */}
        <div className="border-r border-surface-600 overflow-y-auto">
          <ConversationList
            conversations={conversations}
            selectedId={selectedId}
            onSelect={handleSelect}
          />
        </div>

        {/* Right panel */}
        <div className="overflow-hidden">
          {chatLoading ? (
            <div className="flex h-full items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-neon-cyan border-t-transparent" />
            </div>
          ) : (
            <ChatView sessionId={selectedId} messages={messages} />
          )}
        </div>
      </div>
    </div>
  );
}
